import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Divider,
  Paper,
  TextInput,
  Button,
  Title,
  Text,
  Alert,
  Center,
  Loader,
  Stack,
  Group,
  Select,
  PinInput,
  Switch,
  useMantineColorScheme,
  Box
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPhoneCall, IconAlertCircle, IconPhone, IconSun, IconMoon } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';

const MemberLogin = () => {
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneData, setPhoneData] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const [otpMethod, setOtpMethod] = useState('sms');

  const [checkingAuth, setCheckingAuth] = useState(true);
  const phoneForm = useForm({
    initialValues: {
      phone_number: '',
      country_code: '+91',
    },
    validate: {
      phone_number: (value) => {
        if (!value) return 'Phone number is required';
        if (!/^\d{10}$/.test(value)) return 'Phone number must be 10 digits';
        return null;
      },
      country_code: (value) => (!value ? 'Country code is required' : null),
    },
  });

  const [otp, setOtp] = useState('');


  const handlePhoneSubmit = async (values) => {
    setOtpMethod('sms');
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://app-brain-gym.samrruddhi.in/api/member/send-otp', {
        method: 'POST',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send OTP');
      }

      setPhoneData(values);
      setStep('otp');

      if (data.otp) {
        setError(`Development OTP: ${data.otp}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const checkExistingAuth = async () => {
      setCheckingAuth(true);
      try {
        const response = await fetch('https://app-brain-gym.samrruddhi.in/api/member/check-auth', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.authenticated) {
            // User is already logged in, redirect to dashboard
            navigate('/member/dashboard', { replace: true });
            return;
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkExistingAuth();
  }, []); // Empty dependency array to run only once


  const handleOtpSubmit = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the complete OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://app-brain-gym.samrruddhi.in/api/member/verify-otp', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneData.phone_number,
          otp: otp,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid OTP');
      }

      const result = await response.json();

      // Store in localStorage as backup
      localStorage.setItem('token', result.token);
      localStorage.setItem('user', JSON.stringify(result.member));
      localStorage.setItem('userType', 'member');

      // Force navigation with replace to avoid back button issues
      window.location.href = '/member/dashboard';

    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCallOtp = async () => {
    const validation = phoneForm.validate();
    if (validation.hasErrors) {
      return;
    }

    setOtpMethod('call');
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://app-brain-gym.samrruddhi.in/api/member/send-call-otp', {
        method: 'POST',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(phoneForm.values),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send call OTP');
      }

      setPhoneData(phoneForm.values);
      setStep('otp');

      setError('Call OTP initiated! You will receive a call shortly with your verification code.');

      if (data.otp) {
        setError(`Development Call OTP: ${data.otp}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to send call OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };




  const countryCodeOptions = [
    { value: '+91', label: '+91 (India)' },
    { value: '+1', label: '+1 (US/Canada)' },
    { value: '+44', label: '+44 (UK)' },
    { value: '+971', label: '+971 (UAE)' },
    { value: '+353', label: '+353 (Ireland)' },
    { value: '+31', label: '+31 (Netherlands)' },
    { value: '+60', label: '+60 (Malaysia)' },
    { value: '+84', label: '+84 (Vietnam)' },
  ];





  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');

    if (error) {
      switch (error) {
        case 'invalid_link':
          setError('Invalid login link. Please try logging in normally.');
          break;
        case 'expired_link':
          setError('Login link has expired. Please try logging in normally.');
          break;
        case 'login_failed':
          setError('Auto-login failed. Please try logging in normally.');
          break;
        case 'missing_data':
          setError('Login data missing. Please try logging in normally.');
          break;
        default:
          break;
      }
    }
  }, []);




  if (checkingAuth) {
    return (
      <Center style={{ minHeight: '100vh', backgroundColor: 'var(--mantine-color-gray-0)' }}>
        <Stack align="center">
          <Loader size="lg" />
          <Text>Checking authentication...</Text>
        </Stack>
      </Center>
    );
  }
  return (
    <Box style={{
      minHeight: '100vh',
      background: colorScheme === 'dark'
        ? 'linear-gradient(135deg, #1a1b23 0%, #2d1b3d 100%)'
        : 'linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%)'
    }}>


      {/* Header */}
      <Box
        p="md"
        style={{
          borderBottom: `1px solid ${colorScheme === 'dark' ? '#373A40' : '#e9ecef'}`,
          backgroundColor: colorScheme === 'dark' ? '#25262b' : '#ffffff',
          backdropFilter: 'blur(10px)',
        }}
      >
        {/* Mobile Layout */}
        <Box
          style={{ display: 'block' }}
          className="sm:hidden"
        >
          {/* Logo and Title Section */}
          <Group
            justify="center"
            align="center"
            mb="xs"
            gap="md"
          >
            <img
              src="/logo512.png"
              width="40px"
              height="40px"
              style={{ borderRadius: '8px' }}
              alt="Logo"
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text
                size="22px"
                fw={700}
                style={{
                  fontFamily: '"Bahnschrift", "Bahnschrift Condensed", "DIN Alternate", sans-serif',
                  textTransform: 'uppercase',
                  // Bright Gold Gradient (Dark Mode) vs Dark Bronze Gradient (Light Mode)
                  background: colorScheme === 'dark'
                    ? 'linear-gradient(180deg, #FFF9C4 0%, #FFD700 50%, #B8860B 100%)'
                    : 'linear-gradient(180deg, #B8860B 0%, #8B6508 60%, #5D4037 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '0.5px',
                  lineHeight: 1,
                }}
              >
                BRAIN GYM
              </Text>
              <Text
                size="10px"
                fw={600}
                style={{
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: colorScheme === 'dark' ? '#E0E0E0' : '#5F3DC4',
                  marginTop: '2px'
                }}
              >
                Brain Gym Course
              </Text>
            </div>
          </Group>

          {/* Dark Mode Toggle */}
          <Center>
            <Switch
              size="sm"
              color="violet.6"
              onLabel={<IconSun size={14} stroke={2.5} />}
              offLabel={<IconMoon size={14} stroke={2.5} />}
              checked={colorScheme === 'dark'}
              onChange={() => toggleColorScheme()}
            />
          </Center>
        </Box>

        {/* Desktop Layout */}
        <Group
          justify="space-between"
          align="center"
          style={{ display: 'none' }}
          className="sm:flex"
        >
          {/* Left side - Logo and Title */}
          <Group align="center" gap="md">
            <img
              src="/logo512.png"
              width="50px"
              height="50px"
              style={{ borderRadius: '10px' }}
              alt="Logo"
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text
                size="30px"
                fw={700}
                style={{
                  fontFamily: '"Bahnschrift", "Bahnschrift Condensed", "DIN Alternate", sans-serif',
                  textTransform: 'uppercase',
                  // Bright Gold Gradient (Dark Mode) vs Dark Bronze Gradient (Light Mode)

                  background: 'linear-gradient(180deg, #B8860B 0%, #8B6508 60%, #B8860B 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '0.5px',
                  lineHeight: 1,

                  filter: colorScheme === 'dark' ? 'brightness(1.45) saturate(1.15)' : 'none'
                }}
              >
                BRAIN GYM
              </Text>
              <Text
                size="13px"
                fw={600}
                style={{
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  color: colorScheme === 'dark' ? '#D1D5DB' : '#8B6508',
                  marginTop: '3px'
                }}
              >
                Brain Gym Course
              </Text>
            </div>
          </Group>

          {/* Right side - Dark Mode Toggle */}
          <Switch
            size="md"
            color="violet.6"
            onLabel={<IconSun size={16} stroke={2.5} />}
            offLabel={<IconMoon size={16} stroke={2.5} />}
            checked={colorScheme === 'dark'}
            onChange={() => toggleColorScheme()}
          />
        </Group>
      </Box>



      {/* Main Content */}
      <Box style={{
        minHeight: 'calc(100vh - 120px)',
        padding: '1rem 1rem 2rem 1rem',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '1.5rem'
      }}
        className="sm:items-center sm:pt-8"
      >
        <Container size={400}>
          <Paper
            withBorder
            shadow="xl"
            p={40}
            radius="xl"
            style={{
              backgroundColor: colorScheme === 'dark' ? '#2C2E33' : '#ffffff',
              border: `1px solid ${colorScheme === 'dark' ? '#373A40' : '#e9ecef'}`,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Center mb="xl">
              <Box ta="center">
                <Title
                  order={2}
                  mb="xs"
                  style={{
                    color: colorScheme === 'dark' ? '#ffffff' : '#212529',
                    fontWeight: 600,
                    fontSize: '1.75rem'
                  }}
                >
                  Welcome Back
                </Title>
                <Text
                  c="dimmed"
                  size="sm"
                  style={{
                    color: colorScheme === 'dark' ? '#909296' : '#6c757d'
                  }}
                >
                  Access your course dashboard
                </Text>
              </Box>
            </Center>

            {error && (
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                color={error.includes('Development OTP') ? 'blue' : 'red'}
                mb="md"
                radius="md"
              >
                {error}
              </Alert>
            )}

            {step === 'phone' ? (
              <form onSubmit={phoneForm.onSubmit(handlePhoneSubmit)}>
                <Stack gap="lg">
                  <Select
                    label="Country Code"
                    placeholder="Select country code"
                    data={countryCodeOptions}
                    radius="md"
                    size="md"
                    {...phoneForm.getInputProps('country_code')}
                    styles={{
                      input: {
                        backgroundColor: colorScheme === 'dark' ? '#25262b' : '#f8f9fa',
                        border: `1px solid ${colorScheme === 'dark' ? '#373A40' : '#dee2e6'}`,
                        '&:focus': {
                          borderColor: '#7c3aed',
                          boxShadow: '0 0 0 2px rgba(124, 58, 237, 0.2)'
                        }
                      }
                    }}
                  />

                  <TextInput
                    label="Phone Number"
                    placeholder="Enter your 10-digit phone number"
                    leftSection={<IconPhone size={18} stroke={1.5} />}
                    radius="md"
                    size="md"
                    required
                    {...phoneForm.getInputProps('phone_number')}
                    styles={{
                      input: {
                        backgroundColor: colorScheme === 'dark' ? '#25262b' : '#f8f9fa',
                        border: `1px solid ${colorScheme === 'dark' ? '#373A40' : '#dee2e6'}`,
                        '&:focus': {
                          borderColor: '#7c3aed',
                          boxShadow: '0 0 0 2px rgba(124, 58, 237, 0.2)'
                        }
                      }
                    }}
                  />
                  <Button
                    fullWidth
                    loading={loading && otpMethod === 'call'}
                    onClick={handleCallOtp}
                    size="md"
                    radius="md"
                    variant="gradient"
                    gradient={{ from: 'blue', to: 'cyan' }}
                    leftSection={<IconPhoneCall size={18} />}
                    style={{
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(34, 139, 230, 0.3)'
                      }
                    }}
                  >
                    Send Call OTP
                  </Button>
                  <Divider label="OR" labelPosition="center" />
                  <Button
                    type="submit"
                    fullWidth
                    loading={loading && otpMethod === 'sms'}
                    size="md"
                    radius="md"
                    mt="md"
                    style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #e64980 100%)',
                      border: 'none',
                      fontWeight: 600,
                      marginTop: '-10px',
                      paddingTop: '-40px',

                      '&:hover': {
                        background: 'linear-gradient(135deg, #6d28d9 0%, #db2777 100%)',
                        transform: 'translateY(0px)',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
                      }
                    }}
                  >
                    Send Whatsapp OTP
                  </Button>




                  <Text size="xs" ta="center" c="dimmed" mt="sm">
                    Can't receive SMS? Call OTP will call your number with the verification code
                  </Text>
                </Stack>
              </form>
            ) : (
              <Stack gap="lg">
                <Box ta="center">
                  <Text
                    size="sm"
                    c="dimmed"
                    mb="xl"
                    style={{
                      color: colorScheme === 'dark' ? '#909296' : '#6c757d'
                    }}
                  >
                    Enter the 6-digit OTP sent to{' '}
                    <Text component="span" fw={600} c={colorScheme === 'dark' ? '#ffffff' : '#212529'}>
                      {phoneData?.country_code} {phoneData?.phone_number}
                    </Text>
                  </Text>

                  <Center>
                    <PinInput
                      length={6}
                      value={otp}
                      onChange={setOtp}
                      placeholder="●"
                      type="number"
                      size="lg"
                      radius="md"
                      styles={{
                        input: {
                          backgroundColor: colorScheme === 'dark' ? '#25262b' : '#f8f9fa',
                          border: `2px solid ${colorScheme === 'dark' ? '#373A40' : '#dee2e6'}`,
                          '&:focus': {
                            borderColor: '#7c3aed',
                            boxShadow: '0 0 0 2px rgba(124, 58, 237, 0.2)'
                          }
                        }
                      }}
                    />
                  </Center>
                </Box>

                <Button
                  fullWidth
                  loading={loading}
                  onClick={handleOtpSubmit}
                  disabled={otp.length !== 6}
                  size="md"
                  radius="md"
                  style={{
                    background: otp.length === 6
                      ? 'linear-gradient(135deg, #7c3aed 0%, #e64980 100%)'
                      : colorScheme === 'dark' ? '#373A40' : '#e9ecef',
                    border: 'none',
                    fontWeight: 600,
                    opacity: otp.length === 6 ? 1 : 0.6,
                    '&:hover': otp.length === 6 ? {
                      background: 'linear-gradient(135deg, #6d28d9 0%, #db2777 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
                    } : {}
                  }}
                >
                  Verify OTP
                </Button>

                <Button
                  variant="subtle"
                  fullWidth
                  size="md"
                  radius="md"
                  onClick={() => {
                    setStep('phone');
                    setOtp('');
                    setError('');
                  }}
                  style={{
                    color: colorScheme === 'dark' ? '#909296' : '#6c757d',
                    '&:hover': {
                      backgroundColor: colorScheme === 'dark' ? '#373A40' : '#f8f9fa'
                    }
                  }}
                >
                  Change Phone Number
                </Button>
              </Stack>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default MemberLogin;